export type PathColorId = "blue" | "red" | "yellow" | "green" | "purple";

export type NodeShape = "circle" | "triangle" | "diamond" | "square" | "hex";

export type ColorVisionProfile = "standard" | "deuteranopia" | "protanopia" | "tritanopia";

export interface PathVisualToken {
  readonly color: number;
  readonly glowColor: number;
  readonly dimAlpha: number;
  readonly shape: NodeShape;
  readonly label: string;
}

export const GRIDFLOW_TOKENS = {
  palette: {
    background: 0x03060d,
    boardPanel: 0x07111b,
    gridLine: 0x31435a,
    gridLineStrong: 0x597391,
    obstacle: 0x202839,
    textPrimary: 0xf4f7fb,
    textSecondary: 0xaab6c8,
    textMuted: 0x657084,
    danger: 0xff5e6c,
    success: 0xcdf9df,
    buttonFill: 0x101a29,
    buttonStroke: 0x49627d
  },
  paths: {
    blue: {
      color: 0x37a7ff,
      glowColor: 0xa7e2ff,
      dimAlpha: 0.54,
      shape: "circle",
      label: "B"
    },
    red: {
      color: 0xff5b6f,
      glowColor: 0xffa6b3,
      dimAlpha: 0.54,
      shape: "triangle",
      label: "R"
    },
    yellow: {
      color: 0xffd447,
      glowColor: 0xffed9a,
      dimAlpha: 0.58,
      shape: "diamond",
      label: "Y"
    },
    green: {
      color: 0x3fe28c,
      glowColor: 0xb0ffd1,
      dimAlpha: 0.54,
      shape: "square",
      label: "G"
    },
    purple: {
      color: 0xb38cff,
      glowColor: 0xd9c8ff,
      dimAlpha: 0.54,
      shape: "hex",
      label: "P"
    }
  } satisfies Record<PathColorId, PathVisualToken>,
  colorBlindPaths: {
    blue: {
      color: 0x1b9e77,
      glowColor: 0x9ee8d0,
      dimAlpha: 0.56,
      shape: "circle",
      label: "B"
    },
    red: {
      color: 0xd95f02,
      glowColor: 0xffbf82,
      dimAlpha: 0.56,
      shape: "triangle",
      label: "R"
    },
    yellow: {
      color: 0xe6ab02,
      glowColor: 0xffdf7e,
      dimAlpha: 0.6,
      shape: "diamond",
      label: "Y"
    },
    green: {
      color: 0x66a61e,
      glowColor: 0xceec8f,
      dimAlpha: 0.56,
      shape: "square",
      label: "G"
    },
    purple: {
      color: 0x7570b3,
      glowColor: 0xc9c5ff,
      dimAlpha: 0.56,
      shape: "hex",
      label: "P"
    }
  } satisfies Record<PathColorId, PathVisualToken>,
  layout: {
    minViewportWidth: 320,
    outerMargin: 24,
    compactOuterMargin: 18,
    headerHeight: 112,
    footerHeight: 118,
    boardPadding: 0,
    minCellSize: 42,
    maxCellSize: 108,
    buttonHeight: 52,
    buttonMinWidth: 52,
    buttonMaxWidth: 156,
    buttonGap: 16,
    pillRadius: 16,
    safeBottom: 22
  },
  board: {
    cornerRadius: 7,
    cellGapRatio: 0,
    nodeRadiusRatio: 0.34,
    shapeRadiusRatio: 0.12,
    lineWidthRatio: 0.16,
    lineGlowWidthRatio: 0.34,
    celebrationLineGlowWidthRatio: 0.58,
    guideLineWidthRatio: 0.13,
    activeHeadRadiusRatio: 0.15
  },
  typography: {
    family: "Inter, Avenir Next, Helvetica, Arial, sans-serif",
    titleSize: 38,
    labelSize: 13,
    buttonSize: 13,
    nodeSizeRatio: 0.3,
    smallNodeSizeRatio: 0.2
  },
  motion: {
    guideSeconds: 1.4,
    completePulseSeconds: 0.82,
    completePulseAlpha: 0.18,
    reducedMotionPulseAlpha: 0,
    celebrationFlashSeconds: 0.5,
    celebrationParticleTtlMs: 760,
    celebrationParticleSpeedMinRatio: 0.22,
    celebrationParticleSpeedMaxRatio: 0.46,
    celebrationParticleRadiusRatio: 0.05,
    celebrationParticleGravityRatio: 0.18,
    celebrationParticleDrag: 0.86
  }
} as const;

export function resolvePathToken(
  color: string,
  useColorBlindPalette = false,
  colorVisionProfile: ColorVisionProfile = "standard"
): PathVisualToken {
  const palette = useColorBlindPalette ? GRIDFLOW_TOKENS.colorBlindPaths : GRIDFLOW_TOKENS.paths;
  if (isPathColorId(color)) {
    return applyColorVisionProfile(palette[color], colorVisionProfile);
  }

  return applyColorVisionProfile(
    {
      color: 0xf4f7fb,
      glowColor: 0xffffff,
      dimAlpha: 0.5,
      shape: "hex",
      label: color.slice(0, 1).toUpperCase() || "?"
    },
    colorVisionProfile
  );
}

export function parseColorVisionProfile(value: string | null): ColorVisionProfile {
  if (value === "deuteranopia" || value === "protanopia" || value === "tritanopia") {
    return value;
  }
  return "standard";
}

function applyColorVisionProfile(
  token: PathVisualToken,
  profile: ColorVisionProfile
): PathVisualToken {
  if (profile === "standard") {
    return token;
  }
  return {
    ...token,
    color: simulateColorVision(token.color, profile),
    glowColor: simulateColorVision(token.glowColor, profile)
  };
}

function simulateColorVision(
  color: number,
  profile: Exclude<ColorVisionProfile, "standard">
): number {
  const matrix = COLOR_VISION_MATRICES[profile];
  const red = (color >> 16) & 0xff;
  const green = (color >> 8) & 0xff;
  const blue = color & 0xff;
  return (
    (clampColorChannel(red * matrix[0] + green * matrix[1] + blue * matrix[2]) << 16) |
    (clampColorChannel(red * matrix[3] + green * matrix[4] + blue * matrix[5]) << 8) |
    clampColorChannel(red * matrix[6] + green * matrix[7] + blue * matrix[8])
  );
}

function clampColorChannel(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

const COLOR_VISION_MATRICES = {
  deuteranopia: [0.625, 0.375, 0, 0.7, 0.3, 0, 0, 0.3, 0.7],
  protanopia: [0.567, 0.433, 0, 0.558, 0.442, 0, 0, 0.242, 0.758],
  tritanopia: [0.95, 0.05, 0, 0, 0.433, 0.567, 0, 0.475, 0.525]
} as const satisfies Record<Exclude<ColorVisionProfile, "standard">, readonly number[]>;

function isPathColorId(color: string): color is PathColorId {
  return Object.prototype.hasOwnProperty.call(GRIDFLOW_TOKENS.paths, color);
}
